/**
 * Terminal auto-provisioning (2026-07-31).
 *
 * Typing the 43-char terminal token by hand on the device was the problem
 * this replaces. An unpaired device announces itself (its own device-minted
 * terminal id plus a random secret only it holds); an admin sees it online
 * and unregistered, by name and uuid, and approves or rejects it; the device
 * then retrieves its own token by proving it still holds that secret.
 * Nobody types anything.
 *
 * See docs/decisions/2026-07-31-provisioning-secret-and-token-handling.md for
 * the security model (why possession of a secret, not the admin's approval
 * click, is what gates the token release) and
 * docs/decisions/2026-07-31-provisioning-wire-status-mapping.md for why the
 * public wire status has three values while the table tracks four.
 *
 * This file is the admin package's contract for the future provisioning
 * page: `ProvisioningListItem` / `ProvisioningListResponse` is exactly what
 * `GET /api/v1/provision` returns, `ProvisioningApproveBody` /
 * `ProvisioningApproveResponse` / `ProvisioningRejectResponse` are exactly
 * what the two admin decision endpoints exchange.
 */
import { z } from 'zod';
import { isoDateTime, uuid } from './domain.js';
import { SyncDevice } from './sync.js';
/** WebCrypto `getRandomValues(32)` or more, base64-encoded on the device. A
 * generous upper bound guards the endpoint against an oversized body, not
 * against a legitimate secret ever exceeding it. */
export const PROVISIONING_SECRET_MIN_LENGTH = 32;
const PROVISIONING_SECRET_MAX_LENGTH = 512;
const provisioningSecret = z.string().min(PROVISIONING_SECRET_MIN_LENGTH).max(PROVISIONING_SECRET_MAX_LENGTH);
// ---------------------------------------------------------------------------
// Public, unauthenticated wire: POST /api/v1/provision/announce and /claim.
// ---------------------------------------------------------------------------
export const ProvisioningAnnounceRequest = z.object({
    terminalId: uuid,
    name: z.string().trim().min(1).max(64),
    secret: provisioningSecret,
    device: SyncDevice.optional(),
});
/**
 * Wire-level status. Deliberately narrower than the four states the server
 * tracks internally (`ProvisioningStatus` below) — a device announcing after
 * it has already been claimed learns nothing useful beyond "stop trying", so
 * `claimed` is folded into `rejected` here. See the wire-status-mapping
 * decision note.
 */
export const ProvisioningWireStatus = z.enum(['pending', 'approved', 'rejected']);
export const ProvisioningAnnounceResponse = z.object({
    status: ProvisioningWireStatus,
});
export const ProvisioningClaimRequest = z.object({
    terminalId: uuid,
    secret: provisioningSecret,
});
/** Released exactly once. See claimTerminal in packages/server/src/services/provisioning.ts. */
export const ProvisioningClaimResponse = z.object({
    token: z.string().min(1),
    terminalName: z.string().min(1),
});
// ---------------------------------------------------------------------------
// Admin contract — session-authed. This is what packages/admin codes
// against; it does not exist yet and must type-check against these shapes
// without needing to guess at the server's internals.
// ---------------------------------------------------------------------------
/** Full internal lifecycle. The admin list needs all four to tell "still
 * needs a decision" (pending) apart from "decided, waiting on the device"
 * (approved) apart from "nothing left to do" (claimed/rejected). */
export const ProvisioningStatus = z.enum(['pending', 'approved', 'claimed', 'rejected']);
/** One row of `GET /api/v1/provision`. */
export const ProvisioningListItem = z.object({
    terminalId: uuid,
    name: z.string(),
    deviceInfo: SyncDevice.nullable(),
    status: ProvisioningStatus,
    firstSeenAt: isoDateTime,
    lastSeenAt: isoDateTime,
    /** `lastSeenAt` within the last ~90 seconds. */
    online: z.boolean(),
    decidedAt: isoDateTime.nullable(),
    decidedBy: uuid.nullable(),
});
export const ProvisioningListResponse = z.array(ProvisioningListItem);
/** Body of `POST /api/v1/provision/:terminalId/approve`. */
export const ProvisioningApproveBody = z.object({
    /** Overrides the name the device announced. `terminals.name` is UNIQUE, so
     * this is how an admin resolves a collision (two devices both left at the
     * factory-default name) at approval time instead of the request failing
     * outright. Omit to keep the announced name. */
    name: z.string().trim().min(1).max(64).optional(),
});
export const ProvisioningApproveResponse = z.object({
    terminalId: uuid,
    name: z.string(),
    status: z.literal('approved'),
});
export const ProvisioningRejectResponse = z.object({
    terminalId: uuid,
    status: z.literal('rejected'),
});
//# sourceMappingURL=provisioning.js.map